import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { navigate } from 'app/navigation';
import { RESET_ALL_REDUCERS, UPDATE_SOURCE } from 'actions';
import { apiSlice } from 'api/apiSlice';
import { profileApis } from 'api/profile';

/*
 * UPDATE_SOURCE (also dispatched by the re-authenticate flow) and the app-wide
 * RESET_ALL_REDUCERS are handled here as string-listeners; their constants live
 * in the dependency-free `actions/` module to avoid an init-time import cycle.
 * The token/source-saved/reset-token transitions are standard createSlice
 * reducers (auto-generated `session/...` action types).
 */

export const sessionSlice = createSlice({
    name: 'session',
    initialState: {},
    reducers: {
        updateToken: (state, action) => {
            state.token = action.payload.token;
        },
        resetToken: () => ({}),
        sessionUserSaved: (state) => {
            state.profileSaved = true;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(UPDATE_SOURCE, (state, action) => {
                state.source = action.data.source;
            })
            .addCase(RESET_ALL_REDUCERS, () => ({}));
    },
});

export const { updateToken, resetToken, sessionUserSaved } = sessionSlice.actions;

/*
 * Session bootstrap for a new medallion token. Side-effect order matters and
 * is preserved exactly: reset → clear RTK Query cache → token → source →
 * fetch profile → navigate.
 */
export const updateSessionToken = createAsyncThunk(
    'session/updateSessionToken',
    ({ token, redirect, source }, { dispatch }) => {
        dispatch({ type: RESET_ALL_REDUCERS });
        // Clear the RTK Query cache too — RESET_ALL_REDUCERS only resets the
        // plain reducers, not the apiSlice cache. Must run before the getProfile
        // re-fetch below so it does not wipe the freshly-started query.
        dispatch(apiSlice.util.resetApiState());
        dispatch(updateToken({ token }));
        source && dispatch({ type: UPDATE_SOURCE, data: { source } });
        dispatch(
            profileApis.endpoints.getProfile.initiate(undefined, {
                forceRefetch: true,
                subscribe: false,
            })
        );
        navigate(redirect, { replace: true });
    }
);

export const resetSessionToken = resetToken;

export const sessionUserProfileSaved = () => sessionUserSaved();

export default sessionSlice.reducer;
